import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LazyYouTubeEmbed from './LazyYouTubeEmbed';
import { tutorialsData } from '../data/tutorialsData';

const { FiArrowLeft, FiClock, FiBarChart, FiExternalLink, FiShare2, FiBookmark, FiPlay, FiBookOpen } = FiIcons;

const TutorialPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tutorial = tutorialsData.find(t => t.slug === slug);

  useEffect(() => {
    if (!tutorial) {
      navigate('/tutorials');
    }
  }, [tutorial, navigate]);

  if (!tutorial) {
    return null;
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return '#00ff88';
      case 'intermediate':
        return '#ffa500';
      case 'advanced':
        return '#ff6b6b';
      default:
        return '#00ff88';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return '🟢';
      case 'intermediate':
        return '🟡';
      case 'advanced':
        return '🔴';
      default:
        return '🟢';
    }
  };

  // Generate YouTube URLs from video ID
  const getYouTubeUrl = (videoId) => {
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const youtubeUrl = tutorial.videoId ? getYouTubeUrl(tutorial.videoId) : null;

  // Related tutorials (excluding current one)
  const relatedTutorials = tutorialsData
    .filter(t => t.slug !== slug && (t.category === tutorial.category || t.tool === tutorial.tool))
    .slice(0, 3);

  return (
    <div className="app">
      <div className="stars"></div>
      
      <main className="main">
        <div className="container">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: '30px' }}
          >
            <Link
              to="/tutorials"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50px',
                padding: '12px 20px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease',
                width: 'fit-content'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 140, 0, 0.1)';
                e.target.style.borderColor = '#ff8c00';
                e.target.style.boxShadow = '0 0 20px rgba(255, 140, 0, 0.2)';
                e.target.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              <SafeIcon 
                icon={FiArrowLeft} 
                style={{ 
                  fontSize: '18px',
                  transition: 'transform 0.3s ease'
                }} 
              />
              <span>Back to Tutorials</span>
            </Link>
          </motion.div>

          {/* Tutorial Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ marginBottom: '40px' }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                background: 'rgba(255, 140, 0, 0.2)',
                color: '#ff8c00',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                border: '1px solid rgba(255, 140, 0, 0.3)'
              }}>
                {tutorial.tool}
              </span>
              <span style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#b0b0b0',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                {tutorial.category}
              </span>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '6px 12px',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <span>{getDifficultyIcon(tutorial.difficulty)}</span>
                <span style={{
                  color: getDifficultyColor(tutorial.difficulty),
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {tutorial.difficulty}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '6px 12px',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <SafeIcon icon={FiClock} style={{ fontSize: '14px', color: '#ff8c00' }} />
                <span style={{ color: '#b0b0b0', fontSize: '14px', fontWeight: '500' }}>
                  {tutorial.duration}
                </span>
              </div>
            </div>

            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              color: '#ffffff',
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              {tutorial.title}
            </h1>

            <p style={{
              fontSize: '1.2rem',
              color: '#b0b0b0',
              lineHeight: '1.6',
              maxWidth: '800px'
            }}>
              {tutorial.description}
            </p>

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginTop: '24px',
              flexWrap: 'wrap'
            }}>
              <motion.div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(135deg, #ff8c00 0%, #ffa500 100%)',
                  color: '#000',
                  padding: '12px 24px',
                  borderRadius: '50px',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 10px 30px rgba(255, 140, 0, 0.3)',
                  cursor: 'default'
                }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 15px 40px rgba(255, 140, 0, 0.4)'
                }}
              >
                <SafeIcon icon={FiBookOpen} style={{ fontSize: '18px' }} />
                Read Full Tutorial
              </motion.div>

              {youtubeUrl && (
                <motion.a
                  href={youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '50px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    fontWeight: '600',
                    fontSize: '16px',
                    textDecoration: 'none'
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    background: 'rgba(255, 140, 0, 0.1)',
                    borderColor: '#ff8c00'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiPlay} style={{ fontSize: '18px' }} />
                  Watch on YouTube
                </motion.a>
              )}

              <motion.button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  padding: '12px 24px',
                  borderRadius: '50px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontWeight: '600',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
                whileHover={{ 
                  scale: 1.05,
                  background: 'rgba(255, 255, 255, 0.15)'
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigator.share ? navigator.share({
                  title: tutorial.title,
                  url: window.location.href
                }) : navigator.clipboard.writeText(window.location.href)}
              >
                <SafeIcon icon={FiShare2} style={{ fontSize: '18px' }} />
                Share
              </motion.button>
            </div>
          </motion.div>

          {/* Embedded YouTube Video Section */}
          {tutorial.videoId && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ marginBottom: '50px' }}
            >
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '28px' }}>📹</span>
                Video Tutorial
              </h2>
              <div style={{
                border: '2px solid rgba(255, 140, 0, 0.3)',
                borderRadius: '20px',
                overflow: 'hidden',
                background: 'rgba(255, 140, 0, 0.05)',
                padding: '4px'
              }}>
                <LazyYouTubeEmbed 
                  videoId={tutorial.videoId} 
                  title={`${tutorial.title} - Video Tutorial`}
                />
              </div>
            </motion.section>
          )}

          {/* What You'll Achieve Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{
              background: 'rgba(255, 140, 0, 0.1)',
              border: '1px solid rgba(255, 140, 0, 0.3)',
              borderRadius: '20px',
              padding: '30px',
              marginBottom: '40px'
            }}
          >
            <h2 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              color: '#ff8c00',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '24px' }}>🎯</span>
              What You'll Achieve by the End
            </h2>
            <p style={{
              color: '#e0e0e0',
              fontSize: '16px',
              lineHeight: '1.6'
            }}>
              Follow this step-by-step tutorial to master {tutorial.tool} and build practical skills you can apply immediately. 
              Perfect for {tutorial.difficulty.toLowerCase()} users looking to expand their toolkit.
            </p>
          </motion.div>

          {/* Tutorial Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '40px',
              marginBottom: '50px'
            }}
          >
            <div className="prose prose-invert prose-lg max-w-none">
              <ReactMarkdown>{tutorial.content}</ReactMarkdown>
            </div>
          </motion.div>

          {/* Related Tutorials */}
          {relatedTutorials.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <h2 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                Related Tutorials
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px'
              }}>
                {relatedTutorials.map((relatedTutorial) => (
                  <motion.div
                    key={relatedTutorial.slug}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link
                      to={`/tutorials/${relatedTutorial.slug}`}
                      style={{
                        display: 'block',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '20px',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 140, 0, 0.1)';
                        e.target.style.borderColor = 'rgba(255, 140, 0, 0.3)';
                        e.target.style.boxShadow = '0 10px 30px rgba(255, 140, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <h3 style={{
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: '#ffffff',
                        marginBottom: '8px'
                      }}>
                        {relatedTutorial.title}
                      </h3>
                      <p style={{
                        color: '#b0b0b0',
                        fontSize: '14px',
                        marginBottom: '12px',
                        lineHeight: '1.5'
                      }}>
                        {relatedTutorial.description.substring(0, 100)}...
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          color: '#ff8c00',
                          fontWeight: '600'
                        }}>
                          {relatedTutorial.tool}
                        </span>
                        <span style={{ color: '#666' }}>•</span>
                        <span style={{ color: '#b0b0b0' }}>
                          {relatedTutorial.duration}
                        </span>
                        <span style={{ color: '#666' }}>•</span>
                        <span style={{
                          color: getDifficultyColor(relatedTutorial.difficulty)
                        }}>
                          {relatedTutorial.difficulty}
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TutorialPage;